import * as THREE from "three";
import * as TWEEN from "@tweenjs/tween.js";
import Tooltip from "../tooltips/Tooltip";

import GlobalEvent from "js-events-listener";
import { getColour } from "../Customisation";

/**
 * Hotspots are clickable areas with varying actions
 * This class is used as a base for all types
 * Now uses THREE.Sprite for billboard rendering (always faces camera)
 */
export default class BaseHotspot extends THREE.Group {
  renderScale;
  distance;

  /**
   * Creates a hotspot based on the normalised position
   *
   * @param player
   * @param config
   */
  constructor(player, config) {
    super();

    const normPos = new THREE.Vector2(config.u, config.v);

    let _options = config._options ?? {};
    delete config._options;

    const renderScale = _options.scale ?? 1;
    
    // Create sprite material and sprite for billboard rendering
    this.material = new THREE.SpriteMaterial({
      transparent: true,
      color: 0xffffff,
      opacity: 0  // Initialize as transparent
    });
    
    // Track loading state
    this._isLoading = true;
    this._iconLoaded = false;
    
    this.sprite = new THREE.Sprite(this.material);
    this.sprite.scale.set(45 * renderScale, 45 * renderScale, 1);
    // Store the original scale for hover animations
    this.originalScale = {
      x: 45 * renderScale,
      y: 45 * renderScale,
      z: 1
    };
    // Store reference to parent hotspot for raycasting
    this.sprite.userData.hotspot = this;
    this.add(this.sprite);

    // Create a dummy geometry for compatibility with existing code that expects geometry.boundingSphere
    this.geometry = new THREE.BufferGeometry();
    this.geometry.computeBoundingSphere();

    this._config = config;

    this.renderScale = renderScale;
    this.distance = _options.distance ?? 500;

    // NormPos is the normalized position, so need to work out the world coords
    const pos = this.normToWorld(normPos);
    this.position.x = pos.x;
    this.position.y = pos.y;
    this.position.z = pos.z;

    if (player.isSpinning) {
      this.material.opacity = 0;
    }

    this.player = player;

    this.actualScale = this.originalScale.x;
    // Sprites automatically face the camera, no need for lookAt

    this._isHover = false;
    this._isSpinner = false;
    this._isActive = false;

    // Create tooltip to be visible when hovering
    this._hasTooltip = false;

    if (!!config.tooltip && String(config.tooltip).trim() !== "" && !config.permaTooltip) {
      this._hasTooltip = true;

      this._tooltip = new Tooltip(player, config.tooltip);
    }

    GlobalEvent.on(
      "BASE_HOTSPOT_HOVER_" + this._config.uuid,
      ({ isHover, clientX, clientY }) => {
        this.setHover(isHover, true, clientX, clientY);
      },
    );
  }

  /**
   * Converts the normalized position to world coordinates.
   *
   * @param {THREE.Vector2} normPos
   * @return {THREE.Vector3} world coordinates
   */
  normToWorld(normPos) {
    var a = new THREE.Vector3(0, -500, 0);
    var b = new THREE.Euler(0, 2 * normPos.x * Math.PI, normPos.y * Math.PI);

    a.applyEuler(b);

    return a;
  }

  /**
   * Returns the hotspot uuid
   *
   * @returns {string} uuid
   */
  getUUID() {
    return this._config.uuid;
  }

  /**
   * To be called when hover state should change.
   * This changes the opacity of the hotspot.
   *
   * @param {bool} isHover
   */
  setHover(
    isHover,
    fromTourEvent = false,
    clientX = undefined,
    clientY = undefined,
  ) {
    if (this._isHover !== isHover && !this._isActive) {
      // Check if the state has changed, if so, start a tween
      if (!fromTourEvent) {
        if (this._hoveringTimeout) clearTimeout(this._hoveringTimeout);
        this._hoveringTimeout = setTimeout(() => {
          GlobalEvent.emit("BASE_HOTSPOT_HOVER", {
            isHover,
            uuid: this.getUUID(),
            clientX: this._clientX,
            clientY: this._clientY,
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
          });
        }, 500);
      } else if (!!clientX && !!clientY && this._hasTooltip) {
        this._clientX = clientX;
        this._clientY = clientY;
        this._tooltip.onMouseMove({ clientX, clientY });
      }

      this._isHover = isHover;

      // Skip hover effects (color and scale changes) for stickers (tag mode)
      const isSticker = this._config.options?.tag === true || this._config.type === 'sticker';
      
      // Only apply color changes if icon is loaded (to avoid white square flash)
      if (this._iconLoaded && !isSticker) {
        const targetColorHex =
          isHover && !this._isSpinner ? getColour() : 0xffffff;
        const targetColor = new THREE.Color(targetColorHex);

        const colorTween = new TWEEN.Tween(this.material.color).to(
          targetColor,
          600,
        );
        colorTween.easing(TWEEN.Easing.Elastic.Out);
        colorTween.start();
      }

      // Skip scale animation for stickers
      if (!isSticker) {
        let largerScale = 1.3 * this.originalScale.x;
        const targetScale = isHover
          ? { x: largerScale, y: largerScale, z: 1 }
          : this.originalScale;

        const tween = new TWEEN.Tween(this.sprite.scale).to(targetScale, 400);
        tween.easing(TWEEN.Easing.Elastic.Out);
        tween.start();
      }

      if (this.player.sceneManager.activeScene()?.SCENE_NAME === "SceneSplit") {
        if (this._hasTooltip) this._tooltip.setVisibility(false);
        return;
      }

      if (this._hasTooltip) {
        if (!this._isActive) {
          if (["device", "stereo"].includes(this.player.viewerMode)) {
            this._tooltip.alignToCenter();
          }
          this._tooltip.setVisibility(isHover);
        } else {
          this._tooltip.setVisibility(false);
        }
      }
    }
  }

  /**
   * Update coordinates when moved in editor
   */
  move() {
    const normPos = new THREE.Vector2(this._config.u, this._config.v); // NormPos is the normalized position, so need to work out the world coords
    const pos = this.normToWorld(normPos);
    this.position.x = pos.x;
    this.position.y = pos.y;
    this.position.z = pos.z;
    // Sprites automatically face the camera, no need for lookAt
  }

  /**
   * Update the tooltip
   * @param {String} tooltipName
   */
  updateTooltip(tooltipName) {
    this._config = { ...this._config, tooltip: tooltipName };
    const hasText = !!tooltipName && String(tooltipName).trim() !== "";

    if (this._hasTooltip) {
      if (hasText) {
        this._tooltip.update(tooltipName);
      } else {
        this._tooltip.destroy();
        this._tooltip = null;
        this._hasTooltip = false;
      }
    } else if (hasText && !this._config.permaTooltip) {
      this._hasTooltip = true;
      this._tooltip = new Tooltip(this.player, tooltipName);
    }
  }

  /**
   * Update the scale of the sprite
   * Recalculates scale from _options.scale or config options
   */
  updateScale() {
    let renderScale = this._config._options?.scale ?? 1;
    
    // For stickers, recalculate scale from stickerSize or customScale
    if (this._config.type === 'sticker' || this._config.options?.tag === true) {
      if (
        this._config.options.customScale !== null &&
        this._config.options.customScale !== undefined &&
        this._config.options.customScale !== ""
      ) {
        const customScale = parseFloat(this._config.options.customScale);
        renderScale = !isNaN(customScale) && customScale > 0 ? customScale : 2;
      } else if (this._config.options.stickerSize) {
        const sizeMap = { small: 1, medium: 2, large: 3, xlarge: 4 };
        renderScale = sizeMap[this._config.options.stickerSize] ?? 2;
      } else {
        renderScale = this._config._options?.scale ?? 2;
      }
    }
    
    // Update sprite scale
    const baseSize = 45 * renderScale;
    
    // For stickers, maintain aspect ratio if texture is loaded
    const isSticker = this._config.type === 'sticker' || this._config.options?.tag === true;
    if (isSticker && this.material.map && this.material.map.image) {
      const imgWidth = this.material.map.image.width;
      const imgHeight = this.material.map.image.height;
      if (imgWidth > 0 && imgHeight > 0) {
        const aspectRatio = imgWidth / imgHeight;
        
        // Scale based on the larger dimension to maintain aspect ratio
        if (aspectRatio > 1) {
          // Wider than tall - scale width, adjust height
          this.sprite.scale.set(baseSize, baseSize / aspectRatio, 1);
          this.originalScale = {
            x: baseSize,
            y: baseSize / aspectRatio,
            z: 1
          };
        } else {
          // Taller than wide - scale height, adjust width
          this.sprite.scale.set(baseSize * aspectRatio, baseSize, 1);
          this.originalScale = {
            x: baseSize * aspectRatio,
            y: baseSize,
            z: 1
          };
        }
        this.actualScale = Math.max(this.originalScale.x, this.originalScale.y);
      } else {
        // Fallback to square if aspect ratio can't be determined
        this.sprite.scale.set(baseSize, baseSize, 1);
        this.originalScale = {
          x: baseSize,
          y: baseSize,
          z: 1
        };
        this.actualScale = baseSize;
      }
    } else {
      // For non-stickers, use square scale
      this.sprite.scale.set(baseSize, baseSize, 1);
      this.originalScale = {
        x: baseSize,
        y: baseSize,
        z: 1
      };
      this.actualScale = baseSize;
    }
    
    this.renderScale = renderScale;
  }

  /**
   * This is the function that is run when a hotspot is clicked on.
   * This should be overridden by child classes.
   *
   * @param {MouseEvent} event
   */
  action(event) {
    console.info("BaseHotspot: action triggered");
  }

  _clientX;
  _clientY;

  /**
   * Triggered whenever the users mouse moves.
   * This is used to keep the tooltip in position
   */
  onMouseMove(event) {
    if (this._hasTooltip) {
      this._clientX = event.clientX;
      this._clientY = event.clientY;
      this._tooltip.onMouseMove(event);
    }
  }

  /**
   * Called just before the hotspot is to be removed.
   * Used to cleanup.
   */
  destroy() {
    if (this._hasTooltip) {
      this._tooltip.destroy();
    }
    if (this.material.map) {
      this.material.map.dispose();
    }
    this.material.dispose();
    this.geometry.dispose();
  }

  getOpacity() {
    if (!this.active) return this.material.opacity;
  }

  setOpacity(opacity) {
    if (!this.active) {
      // Don't show white square while loading - keep transparent until icon is loaded
      if (this._isLoading || !this._iconLoaded) {
        this.material.opacity = 0;
      } else {
        this.material.opacity = this.player.isSpinning ? 0 : opacity;
      }
    }
  }

  changeToSpinner() {
    // stop hover effects and fadeout
    this._isSpinner = true;

    this._isLoaded = false;
    this.setOpacity(0);

    setTimeout(() => {
      if (!this._isLoaded) {
        this.material.color = new THREE.Color(0xffffff);

        const video = document.createElement("video");
        video.crossOrigin = "anonymous";

        video.src =
          "https://theatro360-manual-assets.s3-us-west-2.amazonaws.com/spinner.webm";
        video.loop = true;
        video.muted = true;
        video.playsinline = true;
        video.load();
        // Only autoplay if not iOS Safari
        const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) &&
          !window.MSStream &&
          /Safari/.test(navigator.userAgent);
        if (!isIOSSafari) {
          video.play();
        }

        const texture = new THREE.VideoTexture(video);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.format = THREE.RGBAFormat;

        if (this.material.map) {
          this.material.map.dispose();
        }
        this.material.map = texture;
        this.material.transparent = true;
        this.material.needsUpdate = true;

        const opacity = { value: 0 };
        const opacityTween = new TWEEN.Tween(opacity).to(
          {
            value: 1,
          },
          1000,
        );
        opacityTween.onUpdate(() => {
          this.setOpacity(opacity.value);
          // Can't get the scaling to work :/
          // this.scale.x = opacity.value * this.actualScale;
          // this.scale.y = opacity.value * this.actualScale;
          // this.scale.z = opacity.value * this.actualScale;
        });
        opacityTween.start();
      }
    }, 500);
  }

  setActive() {
    if (this._hasTooltip) this._tooltip.setVisibility(false);
    this._isActive = true;
    this.setOpacity(0);
  }

  setInactive() {
    this._isActive = false;
    this.setOpacity(1);
  }

  async loadIcon(icon, backup) {
    this.backupIcon = backup;
    this._isLoading = true;
    this._iconLoaded = false;
    
    const media = x => `https://manage.theatro360.com/api/v2/media/${x}`;
    const asset = x => `${window.location.origin}/tour/assets/hotspots/${x}`;
    
    try {
      let url;
      // if custom icon, fetch it
      if (icon) {
        if (icon.url) {
          url = asset(icon.url);
        } else {
          const res = await fetch(media(icon.id));
          if (res.status >= 400)
            throw new Error(`fetch failed with ${res.status}`);
          const data = await res.json();
          url = data.public_url;
          if (url.includes("imgix") || url.includes("imgo")) {
            // For stickers, use contain to fit within bounds; for others use crop
            const isSticker = this._config.type === 'sticker' || this._config.options?.tag === true;
            const fitMode = isSticker ? 'contain' : 'crop';
            url += `&w=256&h=256&fit=${fitMode}`;
          }
        }
      } else {
        url = asset(backup);
      }
      
      // Load texture for sprite with progress callback
      const loader = new THREE.TextureLoader();
      const texture = await new Promise((resolve, reject) => {
        loader.load(
          url, 
          (texture) => {
            // Texture loaded successfully
            texture.minFilter = THREE.LinearFilter;
            texture.magFilter = THREE.LinearFilter;
            resolve(texture);
          },
          undefined, // progress callback - could add loading indicator here
          reject
        );
      });
      
      // Dispose old texture if exists
      if (this.material.map) {
        this.material.map.dispose();
      }
      
      this.material.map = texture;
      this.material.needsUpdate = true;
      this._iconLoaded = true;
      this._isLoading = false;
      
      // For stickers, adjust sprite scale to maintain texture aspect ratio
      const isSticker = this._config.type === 'sticker' || this._config.options?.tag === true;
      if (isSticker && texture.image) {
        const imgWidth = texture.image.width;
        const imgHeight = texture.image.height;
        if (imgWidth > 0 && imgHeight > 0) {
          const aspectRatio = imgWidth / imgHeight;
          const baseSize = 45 * this.renderScale;
          
          // Scale based on the larger dimension to maintain aspect ratio
          if (aspectRatio > 1) {
            // Wider than tall - scale width, adjust height
            this.sprite.scale.set(baseSize, baseSize / aspectRatio, 1);
            this.originalScale = {
              x: baseSize,
              y: baseSize / aspectRatio,
              z: 1
            };
          } else {
            // Taller than wide - scale height, adjust width
            this.sprite.scale.set(baseSize * aspectRatio, baseSize, 1);
            this.originalScale = {
              x: baseSize * aspectRatio,
              y: baseSize,
              z: 1
            };
          }
          this.actualScale = Math.max(this.originalScale.x, this.originalScale.y);
        }
      }
      
      // Show the sprite when icon is loaded
      this.material.opacity = this.player.isSpinning ? 0 : 1;
    } catch (e) {
      console.warn(`Error fetching icon: ${e.message}`);
      // Fallback to backup icon
      try {
        const loader = new THREE.TextureLoader();
        const texture = await new Promise((resolve, reject) => {
          loader.load(
            asset(backup), 
            (texture) => {
              texture.minFilter = THREE.LinearFilter;
              texture.magFilter = THREE.LinearFilter;
              resolve(texture);
            },
            undefined,
            reject
          );
        });
        
        if (this.material.map) {
          this.material.map.dispose();
        }
        
        this.material.map = texture;
        this.material.needsUpdate = true;
        this._iconLoaded = true;
        this._isLoading = false;
        
        // Show the sprite when backup icon is loaded
        this.material.opacity = this.player.isSpinning ? 0 : 1;
      } catch (backupError) {
        console.error(`Error loading backup icon: ${backupError.message}`);
        this._isLoading = false;
        // Keep sprite invisible if both primary and backup fail
        this.material.opacity = 0;
      }
    }
  }

  updateIcon() {
    this.loadIcon(this._config.icon, this.backupIcon);
  }

  /**
   * Check if the icon is loaded and ready to display
   * @returns {boolean} true if icon is loaded
   */
  isIconLoaded() {
    return this._iconLoaded && !this._isLoading;
  }

  /**
   * Preload the icon texture for faster display
   * This can be called early to start loading before the hotspot is needed
   */
  preloadIcon(icon, backup) {
    if (!this._isLoading && !this._iconLoaded) {
      this.loadIcon(icon, backup);
    }
  }

  lookAtMeInterval;
  async lookAtMe(name = "", opts) {
    try {
      const { x, z } = this.position;
      let angle = Math.atan2(z, x);
      // realign (atan2 is from the x axis for some reason)
      angle = Math.PI - angle;
      if (angle < -Math.PI) {
        angle += 2 * Math.PI;
      } else if (angle > Math.PI) {
        angle -= 2 * Math.PI;
      }
      await this.player.setCameraAngle(name, angle, opts);
    } catch (error) {
      console.error("lookAtMe error", error);
    }
  }

  // Compatibility methods for Scene.js scale access
  get scale() {
    return this.sprite.scale;
  }

  set scale(value) {
    if (value && typeof value.set === 'function') {
      this.sprite.scale.copy(value);
    } else if (typeof value === 'number') {
      this.sprite.scale.setScalar(value);
    }
  }
}
